/**
 * v2.56 — PriceUpload page.
 *
 * Dropzone → (store picker) → validate preview → dispatch.
 * Stores are fetched from /api/stores on mount; the user must select at least
 * one before Validar / Despachar are enabled.
 * Gated: if validatePriceFile returns a writes_disabled error the whole page
 * shows an info notice and inputs are disabled.
 *
 * v2.57 — Group picker: when stores have group_code, a GROUP section appears
 * above the individual store list. Clicking a group selects/deselects all its
 * member stores. Group checkboxes are indeterminate when partially selected.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Text,
  Title,
} from '@tremor/react';
import { validatePriceFile, dispatchPriceUpload, getStores } from '../api/priceUpload';
import PriceCsvDropzone from '../components/PriceCsvDropzone';
import type { PriceValidateResponse, Store } from '../api/types';

// ---------------------------------------------------------------------------
// StoreGroupPicker
// ---------------------------------------------------------------------------

interface StoreGroupPickerProps {
  stores: Store[];
  selectedSids: number[];
  setSelectedSids: React.Dispatch<React.SetStateAction<number[]>>;
  disabled?: boolean;
}

function StoreGroupPicker({ stores, selectedSids, setSelectedSids, disabled }: StoreGroupPickerProps) {
  // Collect distinct non-empty group_codes.
  const groups = Array.from(
    new Set(stores.map((s) => s.group_code).filter((g): g is string => !!g)),
  );
  const hasGroups = groups.length > 0;

  function toggleSid(sid: number) {
    setSelectedSids((prev) =>
      prev.includes(sid) ? prev.filter((s) => s !== sid) : [...prev, sid],
    );
  }

  function toggleGroup(group: string) {
    const memberSids = stores.filter((s) => s.group_code === group).map((s) => s.sid);
    const allSelected = memberSids.every((sid) => selectedSids.includes(sid));
    if (allSelected) {
      // Uncheck all members.
      setSelectedSids((prev) => prev.filter((sid) => !memberSids.includes(sid)));
    } else {
      // Add any missing members.
      setSelectedSids((prev) => Array.from(new Set([...prev, ...memberSids])));
    }
  }

  return (
    <div className="flex flex-col gap-1">
      {hasGroups && (
        <div className="mb-2">
          <Text className="text-xs text-gray-500 uppercase tracking-wide mb-1">Grupos</Text>
          {groups.map((group) => {
            const memberSids = stores.filter((s) => s.group_code === group).map((s) => s.sid);
            const allSelected  = memberSids.length > 0 && memberSids.every((sid) => selectedSids.includes(sid));
            const someSelected = memberSids.some((sid) => selectedSids.includes(sid));
            const isIndeterminate = someSelected && !allSelected;
            return (
              <GroupCheckbox
                key={group}
                label={group}
                checked={allSelected}
                indeterminate={isIndeterminate}
                onChange={() => toggleGroup(group)}
                disabled={disabled}
              />
            );
          })}
        </div>
      )}

      <div className={hasGroups ? 'ml-4' : ''}>
        {hasGroups && (
          <Text className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tiendas</Text>
        )}
        {stores.map((s) => (
          <label
            key={s.sid}
            className="flex items-center gap-2 text-sm cursor-pointer select-none"
            aria-label={`Tienda ${s.name}`}
          >
            <input
              type="checkbox"
              checked={selectedSids.includes(s.sid)}
              onChange={() => toggleSid(s.sid)}
              disabled={disabled}
              aria-label={s.name}
            />
            {s.name}
          </label>
        ))}
      </div>
    </div>
  );
}

interface GroupCheckboxProps {
  label: string;
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  disabled?: boolean;
}

function GroupCheckbox({ label, checked, indeterminate, onChange, disabled }: GroupCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={label}
      />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// PriceUpload page
// ---------------------------------------------------------------------------

export default function PriceUpload() {
  const navigate = useNavigate();

  const [file, setFile]                     = useState<File | null>(null);
  const [stores, setStores]                 = useState<Store[]>([]);
  const [selectedSids, setSelectedSids]     = useState<number[]>([]);
  const [storesErr, setStoresErr]           = useState<string | null>(null);
  const [result, setResult]                 = useState<PriceValidateResponse | null>(null);
  const [createMissing, setCreateMissing]   = useState(false);
  const [skipErrors, setSkipErrors]         = useState(false);
  const [writesDisabled, setWritesDisabled] = useState(false);
  const [validating, setValidating]         = useState(false);
  const [dispatching, setDispatching]       = useState(false);
  const [dispatchErr, setDispatchErr]       = useState<string | null>(null);

  // Fetch store list once on mount.
  useEffect(() => {
    getStores()
      .then((list) => setStores(list))
      .catch((e: unknown) => setStoresErr(e instanceof Error ? e.message : String(e)));
  }, []);

  async function onValidate() {
    if (!file || selectedSids.length === 0) return;
    setValidating(true);
    setResult(null);
    setSkipErrors(false);
    setDispatchErr(null);
    try {
      const r = await validatePriceFile(file, selectedSids);
      setResult(r);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === 'writes_disabled') {
        setWritesDisabled(true);
      } else {
        setDispatchErr(msg);
      }
    } finally {
      setValidating(false);
    }
  }

  async function onDispatch() {
    if (!file || selectedSids.length === 0) return;
    setDispatching(true);
    setDispatchErr(null);
    try {
      const { job_id } = await dispatchPriceUpload(file, selectedSids, createMissing, skipErrors);
      navigate(`/price-uploads/${job_id}`);
    } catch (e: unknown) {
      setDispatchErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDispatching(false);
    }
  }

  const hasErrors     = (result?.errors?.length ?? 0) > 0;
  const noStoreSelected = selectedSids.length === 0;
  const canDispatch   = !!file && !!result && !noStoreSelected && (!hasErrors || skipErrors) && !writesDisabled;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Title>Carga de precios</Title>

      {writesDisabled && (
        <Card className="bg-yellow-50 border-yellow-200">
          <Text className="text-yellow-800">
            Las cargas de precios están deshabilitadas en esta instalación. Contacta al administrador.
          </Text>
        </Card>
      )}

      <Card>
        <div className="space-y-4">
          <PriceCsvDropzone onFile={(f) => { setFile(f); setResult(null); setSkipErrors(false); }} />

          {file && (
            <Text className="text-sm text-gray-600">Archivo: {file.name}</Text>
          )}

          {/* Store multi-select with optional group picker */}
          {storesErr ? (
            <Text className="text-sm text-red-600">Error cargando tiendas: {storesErr}</Text>
          ) : stores.length > 0 ? (
            <div>
              <Text className="text-sm font-medium mb-1">Tiendas</Text>
              <StoreGroupPicker
                stores={stores}
                selectedSids={selectedSids}
                setSelectedSids={setSelectedSids}
                disabled={writesDisabled}
              />
              {noStoreSelected && (
                <Text className="text-xs text-red-500 mt-1">Selecciona al menos una tienda</Text>
              )}
            </div>
          ) : null}

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={createMissing}
              onChange={(e) => setCreateMissing(e.target.checked)}
              disabled={writesDisabled}
            />
            Crear partes faltantes
          </label>

          <Button
            onClick={onValidate}
            disabled={!file || noStoreSelected || writesDisabled || validating}
            loading={validating}
            size="sm"
          >
            Validar
          </Button>
        </div>
      </Card>

      {result && (
        <>
          {/* Totals line */}
          <Card>
            <Text className="font-medium">
              {result.total_parts} partes → {result.chunk_count} chunks
            </Text>
          </Card>

          {/* Per-line summary */}
          <Card>
            <Title className="text-base mb-3">Resumen por línea</Title>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Línea</TableHeaderCell>
                  <TableHeaderCell>Partes</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.summary.map((s) => (
                  <TableRow key={s.line}>
                    <TableCell>{s.line}</TableCell>
                    <TableCell>{s.parts}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Errors table (only when errors exist) */}
          {hasErrors && (
            <Card className="border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <Title className="text-base">Errores</Title>
                <Badge color="red">{result.errors.length}</Badge>
              </div>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Fila</TableHeaderCell>
                    <TableHeaderCell>Campo</TableHeaderCell>
                    <TableHeaderCell>Mensaje</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.errors.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell>{e.line_num}</TableCell>
                      <TableCell>{e.field}</TableCell>
                      <TableCell>{e.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={skipErrors}
                  onChange={(e) => setSkipErrors(e.target.checked)}
                  aria-label="Omitir filas con errores"
                />
                Omitir filas con errores
              </label>
            </Card>
          )}

          {dispatchErr && (
            <Card className="bg-red-50 border-red-200">
              <Text className="text-red-700 text-sm">{dispatchErr}</Text>
            </Card>
          )}

          <div className="flex justify-end">
            <Button
              onClick={onDispatch}
              disabled={!canDispatch || dispatching}
              loading={dispatching}
            >
              Despachar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
